import { useMutation, useQuery } from "@apollo/client"
import { Box, ThemeProvider } from "@mui/material"
import MuiForms from "mui-forms"
import React, { JSX, useContext, useEffect, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { useLocation, useNavigate } from "react-router-dom"
import CircularWithValueLabel, { CenterCircular } from "../../components/Loading"
import { UserContext } from "../../contexts/UserContext"
import { CREATE_FORM_MUTATION } from "../../entities/form/graphql/mutations"
import { GET_FORM_QUERY } from "../../entities/form/graphql/queries"
import { MuiFormTheme } from "../../styles/theme"
import FileWithInput from "../../components/Form/CustomField"
import "material-icons/iconfont/material-icons.css"
import { ICustomFieldData, IFormFields } from "../../entities/form/graphql/interface"
import ShiftContext from "../../contexts/ShiftContext"
import { collectFilesFromForm, prepareFormData, uploadFileToS3WithProgress } from "../../entities/form/helper"
import { CREATE_DOCUMENT } from "../../entities/documents/graphql/mutation"
import SiteContext from "../../contexts/SiteContext"

const Form: React.FC = () => {
    const { t } = useTranslation()
    const navigate = useNavigate()
    const location = useLocation()

    // Retrieve the info from the navigation state
    const formId = location.state?.formId // Retrieve the formId from the navigation state
    const entityId = location.state?.entityId
    const entityName = location.state?.entity
    const [schema, setSchema] = useState<{ fields: Array<IFormFields> } | null>(null)
    const [customFieldData, setCustomFieldData] = useState<ICustomFieldData>({})
    const userId = useContext(UserContext)?.user?._id
    const context = useContext(ShiftContext)
    const [createDocument] = useMutation(CREATE_DOCUMENT)
    const [loader, setLoading] = useState(false)
    const [progress, setProgress] = useState(0)
    const siteContext = useContext(SiteContext)

    const { data: formData, loading } = useQuery(GET_FORM_QUERY, {
        variables: { id: formId },
        skip: !formId, // Avoid unnecessary queries if formId is not available
    })

    // Mutation for creating  form
    const [createForm] = useMutation(CREATE_FORM_MUTATION)

    // Memoized version of removeSeverityKey function
    const removeSeverityKey = useMemo(
        () =>
            (obj: any): any => {
                if (Array.isArray(obj)) {
                    return obj.map(removeSeverityKey)
                }

                if (obj && typeof obj === "object") {
                    const newObj = { ...obj }
                    if (newObj?.meta?.displayType?.startsWith("radio")) {
                        newObj.meta = {
                            ...newObj.meta,
                            value: newObj?.meta?.options[1]?.value,
                        }
                    }
                    const { ...rest } = newObj
                    return Object.fromEntries(
                        Object.entries(rest).map(([key, value]) => [key, removeSeverityKey(value)])
                    )
                }

                return obj
            },
        []
    )

    useEffect(() => {
        if (formData?.formTemplate?.schema) {
            const cleanedSchema = removeSeverityKey(formData.formTemplate.schema)
            setSchema(cleanedSchema)
        }
    }, [formData, removeSeverityKey])

    useEffect(() => {
        const style = document.createElement("style")
        style.type = "text/css"
        style.innerHTML = `
            .css-xya7gm {
                width: 250px;
            }
           @media (width: 344px) and (height: 882px) {
                .css-1ynsxom {
                    width: 305px;
                }
            }  
        `
        document.head.appendChild(style)
        return () => {
            document.head.removeChild(style)
        }
    }, [])

    const uploadFilesAndUpdateSchema = async (
        formData: Record<string, any>,
        customFieldData: ICustomFieldData,
        mergedSchemaFields: Array<any>
    ) => {
        const customFiles = Object.values(customFieldData).flatMap((field: { files: File[] }) => field.files)
        const filesToUpload = [...collectFilesFromForm(formData), ...customFiles]
        const progressArray = Array(filesToUpload.length).fill(0)
        let uploadedCount = 0
        setLoading(true)

        const updateCombinedProgress = () => {
            const totalProgress = progressArray.reduce((sum, progress) => sum + progress, 0)
            const combinedProgress = Math.round(totalProgress / filesToUpload.length)
            setProgress(combinedProgress) // Update combined progress
        }
        for (let i = 0; i < filesToUpload.length; i++) {
            const file = filesToUpload[i]
            const record = {
                owner: {
                    id: userId,
                    type: "USER",
                },
                about: {
                    id: context?.vehicleId,
                    type: "VEHICLE",
                },
                fileName: file.name,
            }

            try {
                const { data } = await createDocument({ variables: { record } })
                const uploadUrl = data.createDocument.uploadUrl
                const path = data.createDocument.record?.path
                const thumbnailPath = data.createDocument.record?.thumbnailPath
                const fileName = data.createDocument.record?.fileName

                await uploadFileToS3WithProgress(uploadUrl, file, (percentage) => {
                    progressArray[i] = percentage
                    updateCombinedProgress()
                })

                const fieldToUpdate = mergedSchemaFields.find(
                    (field: { value: { files: Array<{ name: string }> } }) =>
                        field?.value?.files && field?.value?.files.some((f: { name: string }) => f.name === file.name)
                )
                if (fieldToUpdate) {
                    fieldToUpdate.value.files = fieldToUpdate.value.files.map((f: File) =>
                        f.name === file.name ? { path, thumbnailPath, fileName } : f
                    )
                }
                uploadedCount++
                if (uploadedCount === filesToUpload.length) {
                    setLoading(false)
                }
            } catch (error) {
                console.error("Error uploading file:", file.name, error)
            }
        }
    }

    const handleFileWithInputChange = (data: {
        name?: string
        displayName?: string
        text: string
        files: File[]
        severity?: string
    }) => {
        setCustomFieldData((prevData) => ({
            ...prevData,
            [data.name || ""]: {
                // Update the specific field's data
                displayName: data.displayName,
                text: data.text,
                files: data.files,
                severity: data.severity,
            },
        }))
    }

    const fileWithInputFields = formData?.formTemplate?.schema?.fields?.filter((field: IFormFields) =>
        field?.meta?.displayType?.includes("fileWithInput")
    )

    const controls = fileWithInputFields?.reduce(
        (acc: Record<string, JSX.Element>, field: IFormFields, index: number) => {
            if (field.meta.displayType) {
                acc[field?.meta?.displayType] = (
                    <FileWithInput key={index} data={field} onChange={handleFileWithInputChange} />
                )
            }
            return acc
        },
        {}
    )

    const onSubmit = async (formData: Record<string, any>) => {
        // Call the new helper function to prepare form data
        if (formData && schema) {
            const preparedSchema = await prepareFormData(formData, schema, customFieldData)
            await uploadFilesAndUpdateSchema(formData, customFieldData, preparedSchema)
            // Get the current date
            enum EAlertLevel {
                NONE = "None",
                INFO = "Info",
                WARNING = "Warning",
                ACTION_REQUIRED = "Action_Required",
            }
            const alertLevelsToInclude = new Set([EAlertLevel.INFO, EAlertLevel.WARNING, EAlertLevel.ACTION_REQUIRED])

            const alerts = preparedSchema
                .filter(({ severity }) => alertLevelsToInclude.has(severity as EAlertLevel))
                .map(({ name, severity }: any) => ({
                    fieldName: name,
                    alertLevel: severity,
                }))

            const today = new Date()

            // Set the date to tomorrow
            const tomorrow = new Date(today)
            tomorrow.setDate(today.getDate() + 1)
            const payload = {
                completed: true,
                schema: preparedSchema,
                owner: {
                    id: userId,
                    type: "User",
                },
                about: [
                    {
                        id: entityId,
                        type: entityName,
                    },
                ],
                fieldAlerts: alerts,
                completedAt: tomorrow, // will update it to the site timezone accordingly
            }
            const result = await createForm({
                variables: {
                    record: payload,
                },
            })

            if (result?.data?.createForm?.recordId) {
                // navigate to the next screen
                if (siteContext?.site?.tier === "TIER1") {
                    // If the site has the tracking feature, navigate to the tracking page
                    navigate("/tracking")
                } else {
                    navigate("/job-listing")
                }
            } else {
                console.error("Error creating form:", result?.errors)
            }
        }
    }

    if (loading) {
        return <CenterCircular />
    }

    return (
        <Box
            display="flex"
            alignItems="center"
            flexDirection="column"
            sx={{
                top: "12%",
                bgcolor: "primary.main",
                position: "absolute",
                left: "5%",
                borderRadius: 4,
                width: "90%",
                maxHeight: "85vh",
                p: 0,
                overflowY: "auto",
                overflowX: "hidden",
            }}
        >
            {loader && <CircularWithValueLabel progress={progress} />}
            {schema ? (
                <ThemeProvider theme={MuiFormTheme}>
                    <MuiForms
                        name="mui-form"
                        config={{
                            size: "small",
                        }}
                        controls={controls}
                        schema={schema}
                        onSubmit={onSubmit}
                    />
                </ThemeProvider>
            ) : (
                <Box textAlign="center" sx={{ color: "text.secondary" }}>
                    {t("forms.mobile.noSchema")}
                </Box>
            )}
        </Box>
    )
}

export default Form
